import { esc, fmt, pct, RAMP, TREND_C } from './bamsShortsUtils';

/* ---------- daily trend chart (hand-rolled SVG, ported from prototype) ---------- */
export function trendSvg(series, labels, hidden) {
    if (!series.length || !labels.length) return '<p class="pnote">No daily data.</p>';
    const w = 560;
    const pad = { l: 34, r: 26, t: 14, b: 30 };
    const h = 200;
    const shown = series.filter((s) => !hidden.has(s.name));
    const vals = [].concat(...shown.map((s) => s.pts)).filter((v) => v != null);
    const mx = vals.length ? Math.max(1, Math.ceil(Math.max(...vals) + 0.5)) : 1;
    const n = labels.length;
    const X = (i) => pad.l + (n === 1 ? 0 : (i / (n - 1)) * (w - pad.l - pad.r));
    const Y = (v) => pad.t + (1 - v / mx) * (h - pad.t - pad.b);
    let g = '';
    for (let t = 0; t <= mx; t += Math.max(1, Math.round(mx / 4))) {
        g += `<line x1="${pad.l}" y1="${Y(t)}" x2="${w - pad.r}" y2="${Y(t)}" style="stroke:var(--grid)"/>`;
        g += `<text x="${pad.l - 6}" y="${Y(t) + 4}" text-anchor="end" font-family="IBM Plex Mono,monospace" font-size="10" style="fill:var(--muted)">${t}</text>`;
    }
    labels.forEach((l, i) => {
        g += `<text x="${X(i)}" y="${h - pad.b + 16}" text-anchor="middle" font-family="IBM Plex Mono,monospace" font-size="10" style="fill:var(--muted)">${esc(l)}</text>`;
    });
    series.forEach((s, k) => {
        if (hidden.has(s.name)) return;
        const c = TREND_C[k % TREND_C.length];
        const p = s.pts.map((v, i) => (v == null ? null : `${X(i)},${Y(v)}`)).filter(Boolean);
        if (!p.length) return;
        g += `<polyline points="${p.join(' ')}" fill="none" stroke="${c}" stroke-width="2.2" stroke-linejoin="round"/>`;
        s.pts.forEach((v, i) => {
            if (v != null)
                g += `<circle cx="${X(i)}" cy="${Y(v)}" r="2.6" style="fill:var(--panel)" stroke="${c}" stroke-width="2"/>`;
        });
    });
    if (!shown.length)
        g += `<text x="${w / 2}" y="${h / 2}" text-anchor="middle" font-family="Archivo,sans-serif" font-size="12" style="fill:var(--muted)">All facilities hidden — click a legend item to bring one back</text>`;
    const svg = `<svg width="100%" viewBox="0 0 ${w} ${h}" role="img" aria-label="Shorts per order packed by facility and day">${g}</svg>`;
    const legend = `<div class="tlegend">${series
        .map((s, k) => {
            const off = hidden.has(s.name);
            return `<button type="button" class="tlkey${off ? ' off' : ''}" data-series="${esc(s.name)}" aria-pressed="${!off}">
                    <i style="background:${TREND_C[k % TREND_C.length]}"></i>${esc(s.name)}</button>`;
        })
        .join(
            '',
        )}${hidden.size ? `<button type="button" class="tlkey reset" data-series="__all__">show all</button>` : ''}</div>`;
    return svg + legend;
}

/* ---------- daily bar chart (hand-rolled SVG, ported from prototype) ---------- */
export function dayBars(days) {
    if (!days.length) return '';
    const w = 620;
    const bw = Math.min(46, (w - 70) / Math.max(days.length, 1) - 8);
    const h = 180;
    const pad = { l: 34, r: 14, t: 12, b: 44 };
    const mx = Math.max(0.5, ...days.map((e) => (e.packed ? e.lines / e.packed : 0))) * 1.18; // headroom for value labels
    const Y = (v) => pad.t + (1 - v / mx) * (h - pad.t - pad.b);
    let g = '';
    for (let i = 0; i <= 3; i++) {
        const v = (mx * i) / 3;
        g += `<line x1="${pad.l}" y1="${Y(v)}" x2="${w - pad.r}" y2="${Y(v)}" style="stroke:var(--grid)"/>`;
        g += `<text x="${pad.l - 6}" y="${Y(v) + 4}" text-anchor="end" font-family="IBM Plex Mono,monospace" font-size="10" style="fill:var(--muted)">${v.toFixed(1)}</text>`;
    }
    const step = (w - pad.l - pad.r) / days.length;
    days.forEach((e, i) => {
        const v = e.packed ? e.lines / e.packed : 0;
        const x = pad.l + i * step + (step - bw) / 2;
        const y = Y(v);
        const col = v >= 5 ? RAMP[7] : v >= 2 ? RAMP[5] : v >= 0.75 ? RAMP[4] : RAMP[0];
        g += `<rect x="${x}" y="${y}" width="${bw}" height="${Math.max(0, h - pad.b - y)}" fill="${col}"><title>${esc(e.d)}: ${v.toFixed(2)} shorts per packed order</title></rect>`;
        g += `<text x="${x + bw / 2}" y="${y - 4}" text-anchor="middle" font-family="IBM Plex Mono,monospace" font-size="9.5" style="fill:var(--muted)">${v.toFixed(2)}</text>`;
        g += `<text x="${x + bw / 2}" y="${h - pad.b + 14}" text-anchor="middle" font-family="IBM Plex Mono,monospace" font-size="9.5" style="fill:var(--muted)">${esc(e.d.slice(5))}</text>`;
        g += `<text x="${x + bw / 2}" y="${h - pad.b + 27}" text-anchor="middle" font-family="IBM Plex Mono,monospace" font-size="9" style="fill:var(--faint)">${pct(e.short, e.packed)}</text>`;
    });
    return `<svg width="100%" viewBox="0 0 ${w} ${h}" role="img" aria-label="Shorts per packed order by day"><text x="${pad.l}" y="8" font-family="IBM Plex Mono,monospace" font-size="9" style="fill:var(--faint)">SHORTS PER PACKED ORDER · SHORT RATE BELOW</text>${g}</svg>`;
}

export function lineBars(ls) {
    if (ls.length < 2) return '';
    const w = 620;
    const rowH = 26;
    const h = ls.length * rowH + 16;
    const labW = 90;
    const mx = Math.max(0.5, ...ls.map((e) => (e.packed ? e.lines / e.packed : 0)));
    let g = '';
    ls.forEach((e, i) => {
        const v = e.packed ? e.lines / e.packed : 0;
        const y = i * rowH + 8;
        const bw = (w - labW - 70) * (v / mx);
        const col = v >= 5 ? RAMP[7] : v >= 2 ? RAMP[5] : v >= 0.75 ? RAMP[4] : RAMP[0];
        g += `<text x="${labW - 8}" y="${y + 13}" text-anchor="end" font-family="IBM Plex Mono,monospace" font-size="11" style="fill:var(--ink)">${esc(e.l)}</text>`;
        g += `<rect x="${labW}" y="${y + 3}" width="${Math.max(1, bw)}" height="14" fill="${col}"/>`;
        g += `<text x="${labW + bw + 7}" y="${y + 14}" font-family="IBM Plex Mono,monospace" font-size="10.5" style="fill:var(--muted)">${v.toFixed(2)} · ${fmt(e.packed)} packed</text>`;
    });
    return `<svg width="100%" viewBox="0 0 ${w} ${h}" role="img" aria-label="Shorts per packed order by pack line">${g}</svg>`;
}
